import React, { useState } from 'react';
import styled from 'styled-components';
import { usePermissions } from '../hooks/usePermissions';
import { useGetCertificatesQuery, useGetCertificateStatsQuery } from '../store/api/certificatesApi';
import { Card, Badge, Button, Input, EmptyState } from '../styles/components';
import { OrganizationCardsSkeleton } from '../components/common/LoadingStates';
import {
  KeyIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ClockIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import type { CertificateInfo } from '../store/api/certificatesApi';

const CertificatesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const CertificatesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
`;

const HeaderContent = styled.div``;

const CertificatesTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const CertificatesSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[700]};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.gray[200]};
  }
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const StatCard = styled(Card)`
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const StatIcon = styled.div<{ $variant: 'success' | 'warning' | 'error' | 'info' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: ${props => props.theme.borders.radius.lg};
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return props.theme.colors.success[50];
      case 'warning': return props.theme.colors.warning[50];
      case 'error': return props.theme.colors.error[50];
      case 'info': return props.theme.colors.primary[50];
      default: return props.theme.colors.gray[50];
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return props.theme.colors.success[600];
      case 'warning': return props.theme.colors.warning[600];
      case 'error': return props.theme.colors.error[600];
      case 'info': return props.theme.colors.primary[600];
      default: return props.theme.colors.gray[600];
    }
  }};
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    left: ${props => props.theme.spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    height: 1.25rem;
    width: 1.25rem;
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${props => props.theme.spacing.xl};
  width: 100%;
  
  @media (min-width: 768px) {
    width: 20rem;
  }
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[700]};
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
  }
`;

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const CertificatesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.xl};
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const CertificateCard = styled(Card)`
  padding: ${props => props.theme.spacing.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CertificateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: ${props => props.theme.borders.radius.lg};
  background-color: ${props => props.theme.colors.primary[50]};
  color: ${props => props.theme.colors.primary[600]};
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const CertificateName = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const CertificateDetails = styled.div`
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const SerialBadge = styled.span`
  font-family: monospace;
  font-size: ${props => props.theme.fontSizes.xs};
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[700]};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.sm};
  border: 1px solid ${props => props.theme.colors.gray[300]};
`;


const StatsGrid = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
  padding: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
`;

const CertStatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const CertStatValue = styled.div<{ $status?: 'Valid' | 'Warning' | 'Critical' | 'Expired' }>`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => {
    if (props.$status) {
      switch (props.$status) {
        case 'Valid': return props.theme.colors.success[600];
        case 'Warning': return props.theme.colors.warning[600];
        case 'Critical': return props.theme.colors.error[600];
        case 'Expired': return props.theme.colors.error[700];
        default: return props.theme.colors.gray[900];
      }
    }
    return props.theme.colors.gray[900];
  }};
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: white;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const PaginationInfo = styled.div`
  flex: 1;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  min-width: 2.5rem;
  height: 2.5rem;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  background: ${props => props.$active ? props.theme.colors.primary[50] : 'white'};
  color: ${props => props.$active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary[100] : props.theme.colors.gray[50]};
    border-color: ${props => props.$active ? props.theme.colors.primary[300] : props.theme.colors.gray[300]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

export function CertificatesMonitoring() {
  const { isSuperAdmin } = usePermissions();
  const [search, setSearch] = useState('');
  const [serialSearch, setSerialSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20; // Fixed page size

  // Combine search fields for API call
  const searchQuery = search || serialSearch;
  const expiryStatusQuery = statusFilter === 'all' ? undefined : statusFilter;

  const { data, isLoading, error } = useGetCertificatesQuery({
    page: page,
    pageSize: pageSize,
    search: searchQuery,
    expiryStatus: expiryStatusQuery
  });
  const { data: stats } = useGetCertificateStatsQuery();

  const certificates = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  // Only show to super admin support users
  if (!isSuperAdmin) {
    return (
      <CertificatesContainer>
        <EmptyState>
          <ShieldCheckIcon />
          <h3>Access Restricted</h3>
          <p>Certificate monitoring is only available to super admin support users.</p>
        </EmptyState>
      </CertificatesContainer>
    );
  }

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSerialSearchChange = (value: string) => {
    setSerialSearch(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };


  const getCertificateIcon = (certificate: CertificateInfo) => {
    return certificate.isSystemCertificate ? (
      <BuildingOfficeIcon style={{ width: '1.5rem', height: '1.5rem' }} />
    ) : (
      <KeyIcon style={{ width: '1.5rem', height: '1.5rem' }} />
    );
  };

  const getExpiryMessage = (certificate: CertificateInfo) => {
    const { daysUntilExpiry, expiryStatus } = certificate;
    
    if (expiryStatus === 'Expired') {
      return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
    }
    
    if (daysUntilExpiry <= 7) {
      return `Expires in ${daysUntilExpiry} days`;
    }
    
    if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    }
    
    return `Expires in ${daysUntilExpiry} days`;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // TODO: Implement CSV export
      console.log('Exporting certificates...');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <CertificatesContainer>
        <CertificatesHeader>
          <HeaderContent>
            <CertificatesTitle>Certificate Monitoring</CertificatesTitle>
            <CertificatesSubtitle>
              Monitor certificate expiry and usage across all organizations
            </CertificatesSubtitle>
          </HeaderContent>
          <HeaderActions>
            <ExportButton>
              <ArrowDownTrayIcon />
              Export
            </ExportButton>
          </HeaderActions>
        </CertificatesHeader>
        <OrganizationCardsSkeleton />
      </CertificatesContainer>
    );
  }

  if (error) {
    return (
      <CertificatesContainer>
        <EmptyState>
          <ExclamationTriangleIcon />
          <h3>Error Loading Certificates</h3>
          <p>Please try again later.</p>
        </EmptyState>
      </CertificatesContainer>
    );
  }

  return (
    <CertificatesContainer>
      <CertificatesHeader>
        <HeaderContent>
          <CertificatesTitle>Certificate Monitoring</CertificatesTitle>
          <CertificatesSubtitle>
            Monitor certificate expiry and usage across all organizations
          </CertificatesSubtitle>
        </HeaderContent>
        <HeaderActions>
          <ExportButton onClick={handleExport} disabled={isExporting}>
            <ArrowDownTrayIcon />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </ExportButton>
        </HeaderActions>
      </CertificatesHeader>

      {stats && (
        <StatsContainer>
          <StatCard>
            <StatIcon $variant="info">
              <KeyIcon />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.totalCertificates}</StatValue>
              <StatLabel>Total Certificates</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $variant="success">
              <ShieldCheckIcon />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.validCertificates}</StatValue>
              <StatLabel>Valid Certificates</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $variant="warning">
              <ExclamationTriangleIcon />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.warningSertificates}</StatValue>
              <StatLabel>Warning (7-30 days)</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon $variant="error">
              <ExclamationTriangleIcon />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.criticalCertificates + stats.expiredCertificates}</StatValue>
              <StatLabel>Critical & Expired</StatLabel>
            </StatContent>
          </StatCard>
        </StatsContainer>
      )}

      <FilterContainer>
        <FilterGroup>
          <SearchContainer>
            <MagnifyingGlassIcon />
            <SearchInput
              placeholder="Search certificates..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </SearchContainer>
          <SearchContainer>
            <MagnifyingGlassIcon />
            <SearchInput
              placeholder="Search by serial number..."
              value={serialSearch}
              onChange={(e) => handleSerialSearchChange(e.target.value)}
            />
          </SearchContainer>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="Valid">Valid</option>
            <option value="Warning">Warning</option>
            <option value="Critical">Critical</option>
            <option value="Expired">Expired</option>
          </FilterSelect>
        </FilterGroup>

        <ResultsCount>
          <FunnelIcon />
          Showing {certificates.length > 0 ? ((page - 1) * pageSize + 1) : 0}-{Math.min(page * pageSize, totalCount)} of {totalCount} certificates
        </ResultsCount>
      </FilterContainer>

      {certificates.length === 0 ? (
        <EmptyState>
          <KeyIcon />
          <h3>No certificates found</h3>
          <p>Try adjusting your search criteria.</p>
        </EmptyState>
      ) : (
        <CertificatesGrid>
          {certificates.map((certificate) => (
            <CertificateCard key={certificate.name}>
              <CardHeader>
                <CertificateIcon>
                  {getCertificateIcon(certificate)}
                </CertificateIcon>
              </CardHeader>
              
              <CertificateName>
                {certificate.name}
              </CertificateName>
              
              <CertificateDetails>
                Organizations: {certificate.organizationIds.length > 0 ? certificate.organizationIds.join(', ') : ''}
              </CertificateDetails>
              
              <CertificateDetails>
                {certificate.expiryStatus === 'Expired' ? 'Expired on:' : 'Expires on:'} {certificate.expiryDate ? new Date(certificate.expiryDate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : 'Unknown'}
              </CertificateDetails>
              
              <StatsGrid>
                <StatItem>
                  <CertStatLabel>Expires</CertStatLabel>
                  <CertStatValue $status={certificate.expiryStatus}>
                    {getExpiryMessage(certificate)}
                  </CertStatValue>
                </StatItem>
                <StatItem>
                  <CertStatLabel>Serial Number</CertStatLabel>
                  <CertStatValue>
                    {certificate.serialNumber}
                  </CertStatValue>
                </StatItem>
              </StatsGrid>
            </CertificateCard>
          ))}
        </CertificatesGrid>
      )}

      {certificates.length > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} results
          </PaginationInfo>

          <PaginationControls>
            <PageButton
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <ChevronLeftIcon />
            </PageButton>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, page - 2) + i;
              if (pageNumber > totalPages) return null;

              return (
                <PageButton
                  key={pageNumber}
                  $active={pageNumber === page}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </PageButton>
              );
            })}

            <PageButton
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              <ChevronRightIcon />
            </PageButton>
          </PaginationControls>
        </PaginationContainer>
      )}
    </CertificatesContainer>
  );
}